import { useEffect, useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { MdTextSnippet } from 'react-icons/md';
import CircleIcon from 'components/CircleIcon';
import Spinner from 'components/Spinner';
import SectionLabel from 'components/typography/SectionLabel';
import Text from 'components/typography/Text';
import useWhiteLabelSettings from 'hooks/useWhiteLabelSettings';
import { getDocumentTypes, getDocumentUrl } from 'services/CommonService';
import { getPluralForm } from 'shared/helpers/common';

export default function DocumentsTab() {
  const { selectedBooking, textConfig = {} } = useSelector(
    (state) => state.ui,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [documents, setDocumentTypes] = useState([]);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data: documentTypes } = await getDocumentTypes(selectedBooking.reference);
      setDocumentTypes(documentTypes);
      setIsLoading(false);
    } catch (error) {
      console.error(error);
      setIsLoading(false);
    }
  }, [selectedBooking.reference]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  function preprocessDocuments(docs) {
    const maxNumbers = docs.reduce((acc, document) => {
      const updatedAcc = { ...acc };
      if (
        !updatedAcc[document.name] ||
        document.number > updatedAcc[document.name]
      ) {
        updatedAcc[document.name] = document.number;
      }
      return updatedAcc;
    }, {});

    return documents.map((document) => ({
      ...document,
      isMaxNumber: document.number === maxNumbers[document.name],
    }));
  }

  const processedDocuments = preprocessDocuments(documents);

  const { whiteLabelSettings: { primaryColour } = {} } =
    useWhiteLabelSettings();

  const displayDocument = (document, selectedBookingRef) => {
    return <div className="w-full grid grid-cols-[minmax(0,3fr)_minmax(0,1fr)_minmax(0,3fr)] gap-4 items-center">
      {document.primary && (
          <Text className="text-gray-500 md:text-black truncate">
            {document.name}
          </Text>
      )}

      {document.createdDate && (
          <Text className="text-gray-500 md:text-black">
            {new Date(document.createdDate).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })}
          </Text>
      )}

      <a
          href={getDocumentUrl(document, selectedBookingRef)}
          rel="noreferrer"
          target="_blank"
          className="font-bold underline break-words"
      >
        {document.fileName}
      </a>
    </div>
  };

  return (
    <>
      <div className="mt-[40px] mb-[28px] flex items-start">
        <CircleIcon className="mt-1">
          <MdTextSnippet fill={primaryColour || '#FFF'} />
        </CircleIcon>
        <SectionLabel label="Documents" className="ml-[10px] mt-1" />
      </div>
      {textConfig?.documentsText && (
        <div className="text-gray-600 mb-4">
          {textConfig.documentsText}
        </div>
      )}
      {isLoading ? (
        <div className="text-center">
          <Spinner />
          Loading ...{'\n'}This can take up to a minute.
        </div>
      ) : (
        <div>
          {processedDocuments.length <= 0 && !isLoading
            ? 'There are currently no documents for this booking'
            : processedDocuments.map((document) => (
                <div
                  key={document.fileName}
                  className="flex justify-between py-3 md:flex-row flex-col"
                >
                  {document.available ? displayDocument(document, selectedBooking.reference) : <Text className="mr-4 text-gray-500 md:text-black">{`${document.name} Documents are only available ${getPluralForm('day', document.daysBeforeDepartureCanBeViewed, true)} before the departure date`}</Text>}
                </div>
              ))}
        </div>
      )}
    </>
  );
}
